<template>
  <v-container
    fluid
    class="wallet-transactions-page responsive-height"
    :class="{ 'wallet-transactions-page--dark': $vuetify.theme.dark }"
  >
    <section class="wallet-overview-grid">
      <article class="wallet-panel balance-panel">
        <div class="balance-panel__heading">
          <div class="section-title">
            <v-icon size="22">mdi-chart-line</v-icon>
            <span>My Balance</span>
          </div>
          <div class="profit-card">
            <span>Total Profit</span>
            <strong>{{ signedMoney(accountSummary.totalProfit) }} USD</strong>
          </div>
        </div>

        <div class="balance-amount">
          <strong>{{ money(accountSummary.balance) }}</strong>
          <span>USD</span>
        </div>
        <p class="balance-equivalent">Equity: <strong>{{ money(accountSummary.equity) }} USD</strong></p>

        <div class="balance-chart" aria-hidden="true">
          <svg viewBox="0 0 560 72" preserveAspectRatio="none">
            <defs>
              <linearGradient id="walletChartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#f4b400" stop-opacity="0.34" />
                <stop offset="100%" stop-color="#f4b400" stop-opacity="0" />
              </linearGradient>
              <linearGradient id="walletChartLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#d69400" />
                <stop offset="100%" stop-color="#ffc400" />
              </linearGradient>
            </defs>
            <path
              class="balance-chart__area"
              d="M0,48 C35,41 50,36 82,42 C117,50 138,40 171,34 C205,28 225,42 255,27 C276,17 296,-2 317,10 C338,22 349,43 373,34 C400,23 414,23 438,34 C468,50 487,37 509,20 C530,5 545,8 560,10 L560,72 L0,72 Z"
            />
            <path
              class="balance-chart__line"
              d="M0,48 C35,41 50,36 82,42 C117,50 138,40 171,34 C205,28 225,42 255,27 C276,17 296,-2 317,10 C338,22 349,43 373,34 C400,23 414,23 438,34 C468,50 487,37 509,20 C530,5 545,8 560,10"
            />
            <circle class="balance-chart__point" cx="558" cy="10" r="4.5" />
          </svg>
        </div>

        <div class="balance-stats">
          <div v-for="stat in balanceStats" :key="stat.label" class="balance-stat">
            <span>{{ stat.label }}</span>
            <strong>{{ money(stat.value) }} <small>USD</small></strong>
          </div>
        </div>
      </article>

      <article class="wallet-panel quick-actions-panel">
        <div class="quick-actions-panel__heading">
          <h2>Quick Actions</h2>
          <p>You can deposit, withdraw or transfer funds instantly.</p>
        </div>

        <div class="quick-actions">
          <router-link
            v-for="action in quickActions"
            :key="action.title"
            :to="action.to"
            class="quick-action-card"
            :class="`quick-action-card--${action.variant}`"
          >
            <span class="quick-action-card__icon">
              <v-icon size="30">{{ action.icon }}</v-icon>
            </span>
            <strong>{{ action.title }}</strong>
            <span>{{ action.description }}</span>
          </router-link>
        </div>
      </article>
    </section>

    <section class="wallet-panel transaction-history-panel">
      <div class="history-heading">
        <div class="section-title section-title--large">
          <v-icon size="22">mdi-wallet-outline</v-icon>
          <h2>Transaction History</h2>
        </div>
      </div>

      <div class="history-toolbar">
        <div class="transaction-tabs" role="tablist" aria-label="Transaction type">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            class="transaction-tab"
            :class="{ 'transaction-tab--active': activeTab === tab.value }"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

      <div class="transaction-filters">
          <label class="history-field history-search">
            <v-icon size="21">mdi-magnify</v-icon>
            <input
              v-model.trim="search"
              type="search"
              aria-label="Search transactions"
              placeholder="Search transaction ID, account number..."
            />
          </label>

          <button type="button" class="history-field date-field" @click="dateDialog = true">
            <v-icon size="20">mdi-calendar-blank-outline</v-icon>
            <span>{{ dateRangeText }}</span>
            <v-icon size="20">mdi-chevron-down</v-icon>
          </button>

          <div class="history-total history-total--deposit">
            <span class="history-total__icon"><v-icon size="22">mdi-cash-plus</v-icon></span>
            <div>
              <span>Total Deposit</span>
              <strong>${{ money(transactionTotals.deposit) }}</strong>
            </div>
          </div>

          <div class="history-total history-total--withdraw">
            <span class="history-total__icon"><v-icon size="22">mdi-cash-minus</v-icon></span>
            <div>
              <span>Total Withdraw</span>
              <strong>${{ money(transactionTotals.withdraw) }}</strong>
            </div>
          </div>

          <button type="button" class="export-button" @click="exportCsv">
            <span>Export</span>
            <v-icon size="18">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <div class="transaction-table-wrap">
        <table class="transaction-history-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Date &amp; Time</th>
              <th>Acc. No.</th>
              <th>Amount (USD)</th>
              <th>Trx. Fee (USD)</th>
              <th>Net Amount (USD)</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="table-message">
                <v-progress-circular indeterminate size="22" width="2" color="#e3a600" />
                <span>Loading transactions...</span>
              </td>
            </tr>
            <tr v-else-if="!paginatedTransactions.length">
              <td colspan="8" class="table-message">No transactions found.</td>
            </tr>
            <tr v-for="(item, index) in paginatedTransactions" v-else :key="transactionKey(item, index)">
              <td>
                <div class="transaction-type" :class="typeClass(item)">
                  <span><v-icon size="19">{{ typeIcon(item) }}</v-icon></span>
                  <strong>{{ displayType(item) }}</strong>
                </div>
              </td>
              <td>{{ formatDate(item.updated_at || item.created_at) }}</td>
              <td>{{ accountNumber(item) }}</td>
              <td class="amount-cell" :class="amountClass(item)">${{ money(item.amount) }}</td>
              <td>${{ money(transactionFee(item)) }}</td>
              <td>${{ money(netAmount(item)) }}</td>
              <td>
                <span class="status-pill" :class="statusClass(item.status)">
                  <v-icon size="15">{{ statusIcon(item.status) }}</v-icon>
                  {{ displayStatus(item.status) }}
                </span>
              </td>
              <td>
                <button type="button" class="view-button" aria-label="View transaction" @click="showDetails(item)">
                  <v-icon size="19">mdi-eye-outline</v-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="history-footer">
        <span>Showing {{ rangeStart }} to {{ rangeEnd }} of {{ filteredTransactions.length }} entries</span>
        <div class="pagination-controls">
          <label class="per-page-select">
            <select v-model.number="perPage" aria-label="Transactions per page">
              <option :value="10">10 per page</option>
              <option :value="25">25 per page</option>
              <option :value="50">50 per page</option>
            </select>
            <v-icon size="18">mdi-chevron-down</v-icon>
          </label>
          <button type="button" :disabled="page <= 1" @click="page -= 1">
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <button type="button" class="page-number">{{ page }}</button>
          <button type="button" :disabled="page >= totalPages" @click="page += 1">
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>
      </footer>
    </section>

    <v-dialog v-model="dateDialog" max-width="340">
      <v-card class="transaction-date-dialog">
        <v-date-picker v-model="dates" range full-width :max="currentDate" color="#d99a00" />
        <v-card-actions>
          <v-btn text @click="clearDates">Clear</v-btn>
          <v-spacer />
          <v-btn text @click="dateDialog = false">Cancel</v-btn>
          <v-btn color="#d99a00" dark @click="applyDates">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="520" content-class="transaction-details-modal">
      <v-card
        class="transaction-details-dialog"
        :class="{ 'transaction-details-dialog--dark': $vuetify.theme.dark }"
      >
        <v-card-title class="transaction-details-dialog__title">
          <span>Transaction Details</span>
          <v-btn icon small aria-label="Close transaction details" @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedTransaction">
          <dl>
            <template v-for="detail in selectedDetails">
              <dt :key="`${detail.label}-label`">{{ detail.label }}</dt>
              <dd :key="`${detail.label}-value`">{{ detail.value }}</dd>
            </template>
          </dl>
        </v-card-text>
        <v-card-actions class="transaction-details-dialog__actions">
          <v-spacer />
          <v-btn text color="#d99a00" @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { PROFILE__ACTIONS, TRANSACTION__ACTIONS } from "@/store/action-types";

export default {
  name: "TransactionHistory",
  data() {
    return {
      activeTab: "all",
      search: "",
      dates: ["2022-01-01", this.today()],
      appliedDates: ["2022-01-01", this.today()],
      dateDialog: false,
      detailsDialog: false,
      selectedTransaction: null,
      page: 1,
      perPage: 10,
      loading: true,
      tabs: [
        { label: "All", value: "all" },
        { label: "Deposit", value: "deposit" },
        { label: "Withdraw", value: "withdraw" },
        { label: "Transfer", value: "transfer" },
        { label: "Credit In", value: "credit-in" },
        { label: "Credit Out", value: "credit-out" },
      ],
      quickActions: [
        {
          title: "Deposit",
          description: "Add funds to your account",
          icon: "mdi-tray-arrow-down",
          to: "/deposit",
          variant: "deposit",
        },
        {
          title: "Withdraw",
          description: "Withdraw funds to your wallet",
          icon: "mdi-tray-arrow-up",
          to: "/withdraw",
          variant: "withdraw",
        },
        {
          title: "Internal Transfer",
          description: "Transfer between your accounts",
          icon: "mdi-swap-horizontal-bold",
          to: "/internal-transfer",
          variant: "transfer",
        },
      ],
    };
  },
  computed: {
    ...mapState("transactions", ["transactions"]),
    ...mapState("account", ["account_summary"]),
    currentDate() {
      return this.today();
    },
    accountSummary() {
      return this.account_summary || {};
    },
    balanceStats() {
      return [
        { label: "Deposit", value: this.accountSummary.deposit },
        { label: "Withdraw", value: this.accountSummary.withdraw },
        { label: "Equity", value: this.accountSummary.equity },
        { label: "Bonus", value: this.accountSummary.bonus },
      ];
    },
    dateRangeText() {
      if (!this.appliedDates.length) return "All dates";
      return [...this.appliedDates].sort().join(" - ");
    },
    filteredTransactions() {
      const query = this.search.toLowerCase();
      const range = [...this.appliedDates].sort();

      return (this.transactions.allTransactions || []).filter((item) => {
        const type = this.normalizedType(item);
        const matchesTab = this.activeTab === "all" || type === this.activeTab;
        const searchable = [
          item.id,
          item.transaction_id,
          item.trx_id,
          item.acc_no,
          item.from_acc_no,
          item.to_acc_no,
          item.trading_account_no,
          item.trx_type,
          item.status,
        ]
          .filter((value) => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase();
        const matchesSearch = !query || searchable.includes(query);
        const rawDate = item.updated_at || item.created_at;
        const itemDate = rawDate ? String(rawDate).slice(0, 10) : "";
        const matchesDate =
          range.length !== 2 || !itemDate || (itemDate >= range[0] && itemDate <= range[1]);

        return matchesTab && matchesSearch && matchesDate;
      });
    },
    transactionTotals() {
      const query = this.search.toLowerCase();
      const range = [...this.appliedDates].sort();

      return (this.transactions.allTransactions || []).reduce(
        (totals, item) => {
          const status = String(item.status || "").toLowerCase();
          if (!status.includes("approve") && !status.includes("complete")) return totals;

          const searchable = [
            item.id,
            item.transaction_id,
            item.trx_id,
            item.acc_no,
            item.from_acc_no,
            item.to_acc_no,
            item.trading_account_no,
            item.trx_type,
            item.status,
          ]
            .filter((value) => value !== undefined && value !== null)
            .join(" ")
            .toLowerCase();
          if (query && !searchable.includes(query)) return totals;

          const rawDate = item.updated_at || item.created_at;
          const itemDate = rawDate ? String(rawDate).slice(0, 10) : "";
          if (range.length === 2 && itemDate && (itemDate < range[0] || itemDate > range[1])) {
            return totals;
          }

          const type = this.normalizedType(item);
          if (type === "deposit") totals.deposit += this.number(item.amount);
          if (type === "withdraw") totals.withdraw += this.number(item.amount);
          return totals;
        },
        { deposit: 0, withdraw: 0 }
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredTransactions.length / this.perPage));
    },
    paginatedTransactions() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredTransactions.slice(start, start + this.perPage);
    },
    rangeStart() {
      return this.filteredTransactions.length ? (this.page - 1) * this.perPage + 1 : 0;
    },
    rangeEnd() {
      return Math.min(this.page * this.perPage, this.filteredTransactions.length);
    },
    selectedDetails() {
      const item = this.selectedTransaction;
      if (!item) return [];

      return [
        { label: "Type", value: this.displayType(item) },
        { label: "Date & Time", value: this.formatDate(item.updated_at || item.created_at) },
        { label: "Account No.", value: this.accountNumber(item) },
        { label: "Amount", value: `$${this.money(item.amount)} USD` },
        { label: "Transaction Fee", value: `$${this.money(this.transactionFee(item))} USD` },
        { label: "Net Amount", value: `$${this.money(this.netAmount(item))} USD` },
        { label: "Status", value: this.displayStatus(item.status) },
      ];
    },
  },
  watch: {
    activeTab() {
      this.page = 1;
    },
    search() {
      this.page = 1;
    },
    perPage() {
      this.page = 1;
    },
    totalPages(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    ...mapActions("transactions", [TRANSACTION__ACTIONS.TRANSACTIONHISTORY]),
    ...mapActions("account", [PROFILE__ACTIONS.ACCOUNTSUMMARY]),
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    number(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    money(value) {
      return this.number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    signedMoney(value) {
      const amount = this.number(value);
      return `${amount >= 0 ? "+" : "-"}${this.money(Math.abs(amount))}`;
    },
    normalizedType(item) {
      const type = String(item.trx_type || item.type || "").toLowerCase();
      if (type.includes("credit") && type.includes("out")) return "credit-out";
      if (type.includes("credit") && type.includes("in")) return "credit-in";
      if (type.includes("withdraw")) return "withdraw";
      if (type.includes("deposit")) return "deposit";
      if (type.includes("transfer") || type.includes("internal")) return "transfer";
      return type || "transfer";
    },
    displayType(item) {
      const labels = {
        deposit: "Deposit",
        withdraw: "Withdrawal",
        transfer: "Internal Transfer",
        "credit-in": "Credit In",
        "credit-out": "Credit Out",
      };
      return labels[this.normalizedType(item)] || item.trx_type || "Transaction";
    },
    typeIcon(item) {
      const type = this.normalizedType(item);
      if (type === "deposit" || type === "credit-in") return "mdi-arrow-down";
      if (type === "withdraw" || type === "credit-out") return "mdi-arrow-up";
      return "mdi-swap-horizontal";
    },
    typeClass(item) {
      return `transaction-type--${this.normalizedType(item)}`;
    },
    amountClass(item) {
      const type = this.normalizedType(item);
      return type === "withdraw" || type === "credit-out" ? "amount-cell--out" : "amount-cell--in";
    },
    transactionFee(item) {
      return item.transaction_fee !== undefined ? item.transaction_fee : item.trx_fee;
    },
    netAmount(item) {
      if (item.net_amount !== undefined && item.net_amount !== null && item.net_amount !== "") {
        return item.net_amount;
      }
      return Math.max(0, this.number(item.amount) - this.number(this.transactionFee(item)));
    },
    accountNumber(item) {
      return item.acc_no || item.from_acc_no || item.to_acc_no || item.trading_account_no || "N/A";
    },
    displayStatus(status) {
      const value = String(status || "Pending").toLowerCase();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    statusClass(status) {
      const value = String(status || "pending").toLowerCase();
      if (value.includes("approve") || value.includes("complete")) return "status-pill--approved";
      if (value.includes("reject") || value.includes("decline") || value.includes("fail")) return "status-pill--rejected";
      return "status-pill--pending";
    },
    statusIcon(status) {
      const statusClass = this.statusClass(status);
      if (statusClass === "status-pill--approved") return "mdi-check";
      if (statusClass === "status-pill--rejected") return "mdi-close-circle-outline";
      return "mdi-clock-outline";
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(date.getTime())) return String(value);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const period = date.getHours() >= 12 ? "pm" : "am";
      const hour = date.getHours() % 12 || 12;
      return `${day}.${month}.${year} - ${hour}:${minutes} ${period}`;
    },
    transactionKey(item, index) {
      return item.id || item.transaction_id || item.trx_id || `${item.updated_at || item.created_at}-${index}`;
    },
    applyDates() {
      this.appliedDates = this.dates.length === 2 ? [...this.dates].sort() : [];
      this.page = 1;
      this.dateDialog = false;
    },
    clearDates() {
      this.dates = [];
      this.appliedDates = [];
      this.page = 1;
      this.dateDialog = false;
    },
    showDetails(item) {
      this.selectedTransaction = item;
      this.detailsDialog = true;
    },
    exportCsv() {
      const headers = ["Type", "Date & Time", "Account No.", "Amount (USD)", "Fee (USD)", "Net Amount (USD)", "Status"];
      const rows = this.filteredTransactions.map((item) => [
        this.displayType(item),
        this.formatDate(item.updated_at || item.created_at),
        this.accountNumber(item),
        this.money(item.amount),
        this.money(this.transactionFee(item)),
        this.money(this.netAmount(item)),
        this.displayStatus(item.status),
      ]);
      const csv = [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `wallet-transactions-${this.today()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this[TRANSACTION__ACTIONS.TRANSACTIONHISTORY](),
        this[PROFILE__ACTIONS.ACCOUNTSUMMARY](),
      ]);
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.wallet-transactions-page {
  --page-bg: #f7f8fa;
  --panel-bg: #ffffff;
  --panel-soft: #fbfaf6;
  --table-head: #f4f5f7;
  --field-bg: #ffffff;
  --border: #e1e4e8;
  --border-strong: #d7dbe0;
  --text: #15181d;
  --muted: #5f6670;
  --muted-soft: #858b94;
  --row-hover: #fffaf0;
  --purple: #d99a00;
  --blue: #1264f5;
  --green: #009b61;
  --red: #ff263b;
  min-height: calc(100vh - 64px);
  height: calc(100vh - 64px);
  padding: 16px 18px 20px;
  color: var(--text);
  background: var(--page-bg);
  font-family: "Inter", sans-serif;
}

.wallet-transactions-page--dark {
  --page-bg: #030b13;
  --panel-bg: #07111b;
  --panel-soft: #0b1621;
  --table-head: #101b28;
  --field-bg: #08131e;
  --border: #293026;
  --border-strong: #30382c;
  --text: #f5f6f7;
  --muted: #afb6bf;
  --muted-soft: #8c969f;
  --row-hover: #0d1924;
  --purple: #e3a600;
  --blue: #5f82ff;
  --green: #35dc78;
  --red: #ff3f43;
}

.wallet-overview-grid {
  display: grid;
  grid-template-columns: minmax(380px, 5fr) minmax(520px, 7fr);
  gap: 12px;
  margin-bottom: 12px;
}

.wallet-panel {
  background: var(--panel-bg);
  border: 1px solid var(--border);
  border-radius: 9px;
  box-shadow: none;
}

.balance-panel {
  position: relative;
  min-height: 228px;
  padding: 16px 20px 14px;
  overflow: hidden;
}

.wallet-transactions-page--dark .balance-panel {
  background:
    radial-gradient(circle at 78% 18%, rgba(227, 166, 0, 0.07), transparent 48%),
    linear-gradient(135deg, #07121c 0%, #08121b 65%, #061019 100%);
}

.balance-panel__heading,
.history-heading,
.section-title,
.balance-amount,
.transaction-filters,
.history-footer,
.pagination-controls,
.transaction-type,
.status-pill {
  display: flex;
  align-items: center;
}

.balance-panel__heading {
  justify-content: space-between;
}

.section-title {
  gap: 13px;
  font-weight: 600;
  color: var(--text);
}

.section-title .v-icon {
  color: var(--purple);
}

.profit-card {
  display: flex;
  min-width: 132px;
  padding: 10px 14px;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--muted);
  font-size: 12px;
}

.wallet-transactions-page--dark .profit-card {
  background: rgba(8, 18, 27, 0.78);
  border-color: rgba(227, 166, 0, 0.25);
}

.profit-card strong {
  color: var(--green);
  font-size: 13px;
}

.balance-amount {
  margin-top: -1px;
  gap: 10px;
}

.balance-amount strong {
  font-size: 30px;
  line-height: 38px;
  letter-spacing: -0.7px;
}

.balance-amount span {
  margin-top: 8px;
  color: var(--muted);
  font-size: 13px;
}

.balance-equivalent {
  margin: -2px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.balance-equivalent strong {
  color: var(--purple);
  font-weight: 600;
}

.balance-chart {
  height: 38px;
  margin: -2px 0 1px;
}

.balance-chart svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.balance-chart__area {
  fill: url(#walletChartFill);
}

.balance-chart__line {
  fill: none;
  stroke: url(#walletChartLine);
  stroke-width: 1.6;
}

.balance-chart__point {
  fill: #ffc400;
  stroke: rgba(255, 196, 0, 0.28);
  stroke-width: 6;
}

.balance-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  min-height: 56px;
  padding: 10px 0 0;
  background: rgba(247, 249, 255, 0.62);
  border-radius: 9px;
}

.wallet-transactions-page--dark .balance-stats {
  background: rgba(8, 18, 27, 0.88);
}

.balance-stat {
  display: flex;
  min-width: 0;
  padding: 0 13px;
  flex-direction: column;
  gap: 8px;
  border-right: 1px solid var(--border);
}

.balance-stat:last-child {
  border-right: 0;
}

.balance-stat > span {
  color: var(--muted);
  font-size: 11px;
}

.balance-stat strong {
  white-space: nowrap;
  font-size: 13px;
}

.balance-stat small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 400;
}

.quick-actions-panel {
  min-height: 228px;
  padding: 16px 18px 14px;
}

.quick-actions-panel__heading h2,
.section-title--large h2 {
  margin: 0;
  color: var(--text);
  font-size: 16px;
  font-weight: 600;
}

.quick-actions-panel__heading p {
  margin: 7px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 18px;
}

.quick-action-card {
  display: flex;
  min-height: 124px;
  padding: 14px 16px;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid;
  border-radius: 8px;
  text-decoration: none;
  transition: transform 0.18s ease, border-color 0.18s ease;
}

.quick-action-card:hover {
  transform: translateY(-2px);
}

.quick-action-card--deposit {
  background: linear-gradient(135deg, rgba(228, 166, 0, 0.09), rgba(228, 166, 0, 0.02));
  border-color: rgba(205, 149, 0, 0.28);
}

.quick-action-card--withdraw {
  background: linear-gradient(135deg, rgba(228, 166, 0, 0.09), rgba(228, 166, 0, 0.02));
  border-color: rgba(205, 149, 0, 0.28);
}

.quick-action-card--transfer {
  background: linear-gradient(135deg, rgba(228, 166, 0, 0.09), rgba(228, 166, 0, 0.02));
  border-color: rgba(205, 149, 0, 0.28);
}

.wallet-transactions-page--dark .quick-action-card--deposit {
  background: linear-gradient(135deg, rgba(76, 57, 8, 0.36), rgba(10, 20, 29, 0.92));
  border-color: rgba(211, 154, 0, 0.34);
}

.wallet-transactions-page--dark .quick-action-card--withdraw {
  background: linear-gradient(135deg, rgba(76, 57, 8, 0.36), rgba(10, 20, 29, 0.92));
  border-color: rgba(211, 154, 0, 0.34);
}

.wallet-transactions-page--dark .quick-action-card--transfer {
  background: linear-gradient(135deg, rgba(76, 57, 8, 0.36), rgba(10, 20, 29, 0.92));
  border-color: rgba(211, 154, 0, 0.34);
}

.quick-action-card__icon {
  display: grid;
  width: 42px;
  height: 42px;
  margin-bottom: 9px;
  place-items: center;
  border: 1px solid rgba(255, 196, 0, 0.25);
  border-radius: 50%;
}

.quick-action-card--deposit .quick-action-card__icon,
.quick-action-card--withdraw .quick-action-card__icon,
.quick-action-card--transfer .quick-action-card__icon {
  background: linear-gradient(145deg, #7a5900, #4d3900);
}

.quick-action-card__icon .v-icon {
  color: #fff;
}

.quick-action-card strong {
  margin-bottom: 6px;
  color: var(--text);
  font-size: 14px;
}

.quick-action-card > span:last-child {
  max-width: 145px;
  color: var(--muted);
  font-size: 12px;
  line-height: 19px;
}

.transaction-history-panel {
  min-height: 500px;
  padding: 15px 14px 14px;
}

.history-heading {
  padding: 0 2px 12px;
}

.section-title--large {
  gap: 11px;
}

.history-toolbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transaction-tabs {
  display: flex;
  gap: 12px;
}

.transaction-tab {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  color: var(--muted);
  background: var(--panel-soft);
  border: 1px solid var(--border);
  border-radius: 7px;
  font-family: inherit;
  font-size: 11px;
  cursor: pointer;
}

.transaction-tab--active {
  color: var(--purple);
  background: transparent;
  border-color: var(--purple);
}

.wallet-transactions-page--dark .transaction-tab--active {
  color: #ffc400;
}

.transaction-filters {
  gap: 14px;
  align-items: center;
  flex-wrap: nowrap;
}

.history-field {
  height: 40px;
  color: var(--muted);
  background: var(--field-bg);
  border: 1px solid var(--border-strong);
  border-radius: 7px;
}

.history-search {
  display: flex;
  width: 30%;
  min-width: 260px;
  padding: 0 12px;
  align-items: center;
  gap: 9px;
}

.history-field .v-icon {
  color: var(--muted-soft);
}

.history-search input {
  width: 100%;
  color: var(--text);
  outline: 0;
  font-family: inherit;
  font-size: 11px;
}

.history-search input::placeholder {
  color: var(--muted-soft);
}

.date-field {
  display: flex;
  width: 23%;
  min-width: 230px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-family: inherit;
  font-size: 11px;
  cursor: pointer;
}

.history-total {
  display: flex;
  box-sizing: border-box;
  width: 174px;
  min-width: 174px;
  height: 58px;
  margin-top: 0;
  padding: 8px 12px;
  align-items: center;
  gap: 11px;
  background: var(--panel-bg);
  border: 1px solid var(--border);
  border-radius: 7px;
}

.history-total__icon {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  border-radius: 9px;
}

.history-total--deposit .history-total__icon {
  color: var(--green);
  background: rgba(0, 174, 105, 0.09);
  border: 1px solid rgba(0, 174, 105, 0.24);
}

.history-total--deposit .history-total__icon .v-icon {
  color: var(--green);
}

.history-total--withdraw .history-total__icon {
  background: rgba(255, 40, 61, 0.09);
  border: 1px solid rgba(255, 40, 61, 0.24);
}

.history-total--withdraw .history-total__icon .v-icon {
  color: var(--red);
}

.history-total > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
}

.history-total > div span {
  color: var(--muted);
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
}

.history-total strong {
  color: var(--text);
  font-size: 18px;
  line-height: 22px;
  white-space: nowrap;
}

.export-button {
  display: flex;
  height: 40px;
  margin-left: auto;
  padding: 0 15px;
  align-items: center;
  gap: 7px;
  color: var(--purple);
  background: transparent;
  border: 1px solid var(--purple);
  border-radius: 7px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.export-button .v-icon {
  color: var(--purple);
}

.transaction-table-wrap {
  margin-top: 9px;
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
}

.transaction-history-table {
  width: 100%;
  min-width: 990px;
  border-spacing: 0;
  border-collapse: separate;
  table-layout: fixed;
}

.transaction-history-table th,
.transaction-history-table td {
  height: 44px;
  padding: 0 14px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
}

.transaction-history-table th {
  height: 38px;
  color: var(--muted);
  background: var(--table-head);
  font-weight: 400;
}

.transaction-history-table th:nth-child(1) { width: 13%; }
.transaction-history-table th:nth-child(2) { width: 15%; }
.transaction-history-table th:nth-child(3) { width: 13%; }
.transaction-history-table th:nth-child(4) { width: 12%; }
.transaction-history-table th:nth-child(5) { width: 12%; }
.transaction-history-table th:nth-child(6) { width: 14%; }
.transaction-history-table th:nth-child(7) { width: 14%; }
.transaction-history-table th:nth-child(8) { width: 7%; text-align: center; }

.transaction-history-table tbody td {
  color: var(--text);
  background: var(--panel-bg);
  border-top: 1px solid var(--border);
}

.transaction-history-table tbody tr:first-child td {
  border-top: 0;
}

.transaction-history-table tbody tr:hover td {
  background: var(--row-hover);
}

.transaction-history-table td:last-child {
  text-align: center;
}

.transaction-type {
  gap: 8px;
}

.transaction-type > span {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 50%;
}

.transaction-type strong {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  font-weight: 600;
}

.transaction-type--deposit > span,
.transaction-type--credit-in > span {
  background: rgba(0, 174, 105, 0.11);
}

.transaction-type--deposit .v-icon,
.transaction-type--credit-in .v-icon,
.amount-cell--in {
  color: var(--green) !important;
}

.transaction-type--withdraw > span,
.transaction-type--credit-out > span {
  background: rgba(255, 40, 61, 0.11);
}

.transaction-type--withdraw .v-icon,
.transaction-type--credit-out .v-icon,
.amount-cell--out {
  color: var(--red) !important;
}

.transaction-type--transfer > span {
  background: rgba(30, 104, 255, 0.11);
}

.transaction-type--transfer .v-icon {
  color: var(--blue) !important;
}

.amount-cell {
  font-weight: 600;
}

.status-pill {
  width: max-content;
  height: 22px;
  padding: 0 7px;
  gap: 3px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 10px;
}

.status-pill--approved {
  color: var(--green);
  background: rgba(0, 174, 105, 0.07);
  border-color: rgba(0, 174, 105, 0.17);
}

.status-pill--approved .v-icon { color: var(--green); }

.status-pill--pending {
  color: #f6a800;
  background: rgba(255, 169, 0, 0.08);
  border-color: rgba(255, 169, 0, 0.18);
}

.status-pill--pending .v-icon { color: #f6a800; }

.status-pill--rejected {
  color: var(--red);
  background: rgba(255, 40, 61, 0.07);
  border-color: rgba(255, 40, 61, 0.17);
}

.status-pill--rejected .v-icon { color: var(--red); }

.view-button,
.pagination-controls > button {
  display: inline-grid;
  width: 36px;
  height: 34px;
  place-items: center;
  color: var(--muted);
  background: var(--panel-soft);
  border: 1px solid var(--border);
  border-radius: 7px;
  cursor: pointer;
}

.view-button .v-icon,
.pagination-controls > button .v-icon {
  color: var(--muted-soft);
}

.table-message {
  height: 190px !important;
  text-align: center !important;
  color: var(--muted) !important;
}

.table-message span {
  margin-left: 8px;
}

.history-footer {
  min-height: 58px;
  padding: 13px 6px 0;
  justify-content: space-between;
  color: var(--muted);
  font-size: 11px;
}

.pagination-controls {
  gap: 10px;
}

.per-page-select {
  position: relative;
  display: flex;
  width: 144px;
  height: 38px;
  align-items: center;
  background: var(--field-bg);
  border: 1px solid var(--border);
  border-radius: 7px;
}

.per-page-select select {
  width: 100%;
  height: 100%;
  padding: 0 35px 0 13px;
  color: var(--text);
  background: transparent;
  border: 0;
  outline: 0;
  appearance: none;
  font-family: inherit;
  font-size: 11px;
}

.per-page-select .v-icon {
  position: absolute;
  right: 10px;
  color: var(--muted-soft);
  pointer-events: none;
}

.pagination-controls > button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pagination-controls > .page-number {
  color: #15120a;
  background: linear-gradient(180deg, #ffc826, #e4a500);
  border-color: #e4a500;
}

.transaction-date-dialog {
  color: #090d28;
  background: #fff !important;
}

.transaction-details-dialog {
  overflow: hidden;
  color: #10182a;
  background: #fff !important;
  border: 1px solid #dfe4ee;
  border-radius: 12px !important;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.28) !important;
}

.transaction-details-dialog--dark {
  color: #f4f6fb;
  background: #071426 !important;
  border-color: #1b3452;
}

.transaction-details-dialog__title {
  display: flex;
  padding: 20px 22px 14px;
  align-items: center;
  justify-content: space-between;
  color: inherit;
  font-size: 20px;
  font-weight: 600;
}

.transaction-details-dialog__title .v-icon {
  color: #75819c;
}

.transaction-details-dialog .v-card__text {
  padding: 0 22px 8px;
  color: inherit !important;
}

.transaction-details-dialog dl {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  margin: 0;
}

.transaction-details-dialog dt,
.transaction-details-dialog dd {
  margin: 0;
  padding: 10px 0;
  border-bottom: 1px solid #dfe4ee;
}

.transaction-details-dialog dt {
  color: #61708c;
  font-weight: 500;
}

.transaction-details-dialog dd {
  color: inherit;
  text-align: right;
  font-weight: 600;
}

.transaction-details-dialog--dark dt,
.transaction-details-dialog--dark dd {
  border-bottom-color: #1b3452;
}

.transaction-details-dialog--dark dt {
  color: #9eabc1;
}

.transaction-details-dialog__actions {
  min-height: 58px;
  padding: 8px 18px 12px;
  background: #f8f9fc;
}

.transaction-details-dialog--dark .transaction-details-dialog__actions {
  background: #0a192d;
}

@media (max-width: 1400px) {
  .wallet-transactions-page {
    padding: 18px;
  }

  .wallet-overview-grid {
    grid-template-columns: minmax(350px, 5fr) minmax(450px, 7fr);
  }

  .quick-actions {
    gap: 10px;
  }

  .quick-action-card {
    padding: 18px 16px;
  }

  .transaction-filters {
    flex-wrap: nowrap;
  }

  .history-search {
    width: auto;
    min-width: 220px;
    flex: 1 1 auto;
  }

  .date-field {
    width: 220px;
    min-width: 220px;
  }

  .history-total {
    margin-top: 0;
  }
}

@media (max-width: 1100px) {
  .wallet-overview-grid {
    grid-template-columns: 1fr;
  }

  .history-total {
    flex: 1 1 180px;
  }

  .transaction-filters {
    flex-wrap: wrap;
  }

  .export-button {
    margin-left: 0;
  }
}

@media (max-width: 700px) {
  .wallet-transactions-page {
    height: calc(100vh - 56px);
    padding: 12px;
  }

  .balance-panel,
  .quick-actions-panel {
    padding: 17px;
  }

  .balance-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 0;
    padding-bottom: 14px;
  }

  .balance-stat:nth-child(2) {
    border-right: 0;
  }

  .quick-actions {
    grid-template-columns: 1fr;
  }

  .quick-action-card {
    min-height: 126px;
  }

  .transaction-tabs {
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .history-search,
  .date-field {
    width: 100%;
    min-width: 0;
  }

  .history-total {
    width: 100%;
    flex-basis: 100%;
  }

  .history-footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .pagination-controls {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
